/**
 * Relations Service - API calls voor relaties
 */

import apiFetch from './api';

export enum RelatieType {
  KLANT_PARTICULIER = 'Klant Particulier',
  KLANT_BEDRIJF = 'Klant Bedrijf',
  LEVERANCIER = 'Leverancier',
  BEIDE = 'Beide',
}

export interface Relation {
  id: number;
  relatienr?: string;
  type: RelatieType;
  aanhef?: string;
  bedrijfsnaam: string;
  contactpersoon?: string;
  contactpersoon_aanhef?: string;
  contactpersoon_voornaam?: string;
  contactpersoon_tussenvoegsel?: string;
  contactpersoon_achternaam?: string;
  voornaam?: string;
  tussenvoegsel?: string;
  achternaam?: string;
  straat?: string;
  huisnummer?: string;
  postcode?: string;
  plaats?: string;
  land: string;
  telefoon?: string;
  mobiel?: string;
  email?: string;
  website?: string;
  kvk_nummer?: string;
  btw_nummer?: string;
  iban?: string;
  betalingstermijn?: number;
  kredietlimiet?: number;
  is_zzp: boolean;
  notities?: string;
  actief: boolean;
  klant_vanaf?: string;
  created_at: string;
  updated_at?: string;
  boot_namen?: string[];  // List of boat names for this relation
}

export interface RelationCreate {
  relatienr?: string;
  type: RelatieType;
  aanhef?: string;
  bedrijfsnaam: string;
  contactpersoon?: string;
  contactpersoon_aanhef?: string;
  contactpersoon_voornaam?: string;
  contactpersoon_tussenvoegsel?: string;
  contactpersoon_achternaam?: string;
  voornaam?: string;
  tussenvoegsel?: string;
  achternaam?: string;
  straat?: string;
  huisnummer?: string;
  postcode?: string;
  plaats?: string;
  land?: string;
  telefoon?: string;
  mobiel?: string;
  email?: string;
  website?: string;
  kvk_nummer?: string;
  btw_nummer?: string;
  iban?: string;
  betalingstermijn?: number;
  kredietlimiet?: number;
  is_zzp?: boolean;
  notities?: string;
  actief?: boolean;
  klant_vanaf?: string;
}

export interface RelationListResponse {
  items: Relation[];
  total: number;
  page: number;
  page_size: number;
}

export interface RelationFilters {
  skip?: number;
  limit?: number;
  offset?: number;
  actief?: boolean;
  zoek?: string;
  type?: string;
  sorteer?: string;
  richting?: string;
}

// Helper functie om lege strings te verwijderen uit data
const cleanRelationData = (data: any): any => {
  const cleaned: any = {};
  for (const key in data) {
    const value = data[key];
    // Verwijder lege strings voor datum velden, converteer naar null
    if (value === '' && (key === 'klant_vanaf' || key.includes('datum'))) {
      // Stuur helemaal niet mee als het leeg is
      continue;
    }
    // Verwijder andere lege strings ook
    if (value === '') {
      continue;
    }
    cleaned[key] = value;
  }
  return cleaned;
};

export const relationsService = {
  async getAll(filters: RelationFilters = {}): Promise<RelationListResponse> {
    const params = new URLSearchParams();

    if (filters.skip !== undefined) params.append('skip', filters.skip.toString());
    if (filters.offset !== undefined) params.append('skip', filters.offset.toString());
    if (filters.limit !== undefined) params.append('limit', filters.limit.toString());
    if (filters.actief !== undefined) params.append('actief', filters.actief.toString());
    if (filters.zoek) params.append('zoek', filters.zoek);
    if (filters.type) params.append('type', filters.type);
    if (filters.sorteer) params.append('sorteer', filters.sorteer);
    if (filters.richting) params.append('richting', filters.richting);

    return apiFetch(`/api/v1/relations?${params.toString()}`);
  },

  async getById(id: number): Promise<Relation> {
    return apiFetch(`/api/v1/relations/${id}`);
  },

  async create(data: RelationCreate): Promise<Relation> {
    const cleanedData = cleanRelationData(data);
    return apiFetch('/api/v1/relations', {
      method: 'POST',
      body: JSON.stringify(cleanedData),
    });
  },

  async update(id: number, data: Partial<RelationCreate>): Promise<Relation> {
    const cleanedData = cleanRelationData(data);
    return apiFetch(`/api/v1/relations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(cleanedData),
    });
  },

  async delete(id: number, options?: { delete_boats?: boolean; set_owner_unknown?: boolean }): Promise<void> {
    const params = new URLSearchParams();
    if (options?.delete_boats) params.append('delete_boats', 'true');
    if (options?.set_owner_unknown) params.append('set_owner_unknown', 'true');

    const url = `/api/v1/relations/${id}${params.toString() ? '?' + params.toString() : ''}`;
    return apiFetch(url, {
      method: 'DELETE',
    });
  },

  async searchCompany(bedrijfsnaam: string): Promise<{
    naam?: string;
    straat?: string;
    huisnummer?: string;
    adres?: string; // Backwards compatibility
    postcode?: string;
    plaats?: string;
    email?: string;
    telefoon?: string;
    mobiel?: string;
    website?: string;
    btw_nummer?: string;
    kvk_nummer?: string;
  }> {
    const params = new URLSearchParams();
    params.append('bedrijfsnaam', bedrijfsnaam);
    return apiFetch(`/api/v1/relations/search-company?${params.toString()}`, {
      method: 'POST',
    });
  },
};
